import React, { createContext, useContext, useEffect } from "react";
import { SITES } from "@/config/sites";

const SiteThemeContext = createContext(null);

// Convert #RRGGBB to "H S% L%" for CSS HSL variables.
function hexToHsl(hex) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const lig = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = lig > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / d + 2;
        break;
      default:
        hue = (r - g) / d + 4;
    }
    hue *= 60;
  }
  return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(lig * 100)}%`;
}

export function SiteThemeProvider({ siteKey, children }) {
  const site = SITES[siteKey];

  useEffect(() => {
    if (!site) return;
    const root = document.documentElement;
    const t = site.theme;
    root.style.setProperty("--background", hexToHsl(t.background));
    root.style.setProperty("--foreground", hexToHsl(t.text));
    root.style.setProperty("--card", hexToHsl(t.surface));
    root.style.setProperty("--card-foreground", hexToHsl(t.text));
    root.style.setProperty("--popover", hexToHsl(t.surface));
    root.style.setProperty("--popover-foreground", hexToHsl(t.text));
    root.style.setProperty("--primary", t.primaryHsl);
    root.style.setProperty("--primary-foreground", t.dark ? "222 47% 6%" : "0 0% 100%");
    root.style.setProperty("--secondary", hexToHsl(t.surface));
    root.style.setProperty("--secondary-foreground", hexToHsl(t.text));
    root.style.setProperty("--muted", hexToHsl(t.surface));
    root.style.setProperty("--muted-foreground", hexToHsl(t.muted));
    root.style.setProperty("--accent", hexToHsl(t.surface));
    root.style.setProperty("--accent-foreground", hexToHsl(t.text));
    root.style.setProperty("--border", t.dark ? "217 33% 20%" : "214 32% 88%");
    root.style.setProperty("--input", t.dark ? "217 33% 20%" : "214 32% 88%");
    root.style.setProperty("--ring", t.primaryHsl);
    root.style.setProperty("--radius", t.radius);
    root.style.setProperty("--font-heading", t.fontHeading);
    root.style.setProperty("--font-body", t.fontBody);
    root.style.setProperty("--theme-accent", t.accent);
    root.classList.toggle("dark", !!t.dark);
    document.body.style.backgroundColor = t.background;
    document.body.style.color = t.text;
  }, [site]);

  if (!site) return children;
  return (
    <SiteThemeContext.Provider value={site}>
      <div className="theme-scope min-h-screen" data-testid={`site-scope-${siteKey}`}>
        {children}
      </div>
    </SiteThemeContext.Provider>
  );
}

export const useSite = () => useContext(SiteThemeContext);
